import { Formik, useFormikContext, type FormikHelpers } from "formik";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FC,
  type KeyboardEvent,
  type ReactNode
} from "react";
import { toast } from "sonner";

import { Spinner } from "@/components/ui/spinner";

import {
  clearSessionDraft,
  readSessionDraft,
  writeSessionDraft
} from "@/lib/sessionDraft";
import { ForeignNationalsAPI } from "../../../api/foreignNationals";
import {
  isNoticeWorkflow,
  noticeWorkflowLabel
} from "../../clientProfile/tabs/cases/useCreateCaseForm";
import {
  TypeformActions,
  TypeformQuestion,
  TypeformShell
} from "../../foreignNationals/components/TypeformShell";

import { useTypeformFlow } from "../../foreignNationals/hooks/useTypeformFlow";
import { useSubmitNewCase } from "../hooks/useSubmitNewCase";
import type { ClientMode, NewCaseStep, NewCaseValues } from "../types";
import {
  baseVisaOf,
  canAdvance,
  decodeNewCaseDraft,
  encodeNewCaseDraft,
  initialNewCaseValues,
  newCaseDraftKey,
  noticeFilledFrom,
  noticeFilledResets,
  noticeLabelOf,
  noticePrefillUpdates,
  noticeVisaFrom,
  petitionerHint,
  stepsFor,
  toSelectedClient,
  validateNewCaseValues
} from "../utils";
import { CLIENT_MODE_OPTIONS, ClientModeStep } from "./ClientModeStep";
import { DetailsStep } from "./DetailsStep";
import { ExistingClientStep } from "./ExistingClientStep";
import { DialogQuestion, NewCaseDialogFrame } from "./NewCaseDialogFrame";
import { NewClientStep, type ClientError } from "./NewClientStep";
import { NoticeStep } from "./NoticeStep";
import { PetitionerStep } from "./PetitionerStep";
import type { CustomCaseType } from "../../customCaseTypes/types";
import { NoticeVisaStep, WorkflowStep } from "./WorkflowStep";

export type NewCaseLayout = "page" | "dialog";

type StepsProps = {
  initialClientId: string | null;
  clientError: ClientError | null;
  setClientError: (error: ClientError | null) => void;
  layout: NewCaseLayout;
  firmName?: string | null;
  onCancel?: () => void;
  customTypes?: readonly CustomCaseType[];
};

/** Keeps the answers for the tab as they change (restored at mount above). */
const KeepDraft: FC<{ draftKey: string }> = ({ draftKey }) => {
  const { values } = useFormikContext<NewCaseValues>();
  const encoded = encodeNewCaseDraft(values);
  useEffect(() => writeSessionDraft(draftKey, encoded), [draftKey, encoded]);
  return null;
};

const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

const WizardSteps: FC<StepsProps> = ({
  initialClientId,
  clientError,
  setClientError,
  layout,
  firmName,
  onCancel,
  customTypes
}) => {
  const { values, setFieldValue, setValues, submitForm, isSubmitting } =
    useFormikContext<NewCaseValues>();
  const [clientQuery, setClientQuery] = useState("");
  const [isLoadingClient, setIsLoadingClient] = useState(
    Boolean(initialClientId)
  );
  const [clientTotal, setClientTotal] = useState<number | null>(null);
  const frameRef = useRef<HTMLDivElement | null>(null);

  // In the modal, keys like 1 and 2 work before anything is clicked.
  useEffect(() => {
    const frame = frameRef.current;
    if (frame && !frame.contains(document.activeElement))
      frame.focus({ preventScroll: true });
  }, []);

  const steps = stepsFor(values);
  const flow = useTypeformFlow<NewCaseStep>({
    steps,
    isComplete: (step) => canAdvance(step, values)
  });
  const { confirm, unconfirm, focusStep } = flow;

  // `?clientId=` answers the first two questions and opens on the workflow.
  useEffect(() => {
    if (!initialClientId) return undefined;
    let cancelled = false;
    ForeignNationalsAPI.get(initialClientId)
      .then((fn) => {
        if (cancelled) return;
        setValues((prev) => ({
          ...prev,
          mode: "existing",
          client: toSelectedClient(fn)
        }));
        confirm("clientMode");
        confirm("client");
      })
      .catch(() => {
        if (!cancelled) toast.error("Couldn't load that client");
      })
      .finally(() => {
        if (!cancelled) setIsLoadingClient(false);
      });
    return () => {
      cancelled = true;
    };
  }, [confirm, initialClientId, setValues]);

  // A new workflow invalidates the original filing chosen for the old one,
  // unless the notice read names it.
  const previousWorkflowRef = useRef(values.workflow);
  const readVisa = noticeVisaFrom(
    values.noticePreview?.status === "ready"
      ? values.noticePreview.prefill?.visa
      : null
  );
  useEffect(() => {
    if (previousWorkflowRef.current === values.workflow) return;
    previousWorkflowRef.current = values.workflow;
    const notice = isNoticeWorkflow(values.workflow);
    setFieldValue("noticeVisa", notice ? readVisa : "", false);
    unconfirm("noticeVisa");
    if (!notice) {
      setFieldValue("noticeFile", null, false);
      setFieldValue("noticePreview", null, false);
    }
  }, [setFieldValue, unconfirm, values.workflow]);

  // A notice read fills in what it knows, once per file. Replacing or
  // removing the notice takes back what it filled that the attorney left alone.
  const appliedNoticeRef = useRef<string | null>(null);
  const noticeFilledRef = useRef<ReturnType<typeof noticeFilledFrom>>({});
  useEffect(() => {
    const preview = values.noticePreview;
    if (!values.noticeFile) {
      appliedNoticeRef.current = null;
      const resets = noticeFilledResets(values, noticeFilledRef.current);
      noticeFilledRef.current = {};
      if (Object.keys(resets).length)
        setValues((prev) => ({ ...prev, ...resets }), false);
      return;
    }
    if (preview?.status !== "ready" || !preview.prefill) return;
    if (appliedNoticeRef.current === preview.checksum) return;
    appliedNoticeRef.current = preview.checksum;
    const updates = noticePrefillUpdates(
      values,
      preview.prefill,
      noticeFilledRef.current
    );
    noticeFilledRef.current = noticeFilledFrom(updates);
    if (Object.keys(updates).length)
      setValues((prev) => ({ ...prev, ...updates }), false);
  }, [setValues, values.noticeFile, values.noticePreview]);

  // A failed client create sends the user back to the client question.
  useEffect(() => {
    if (clientError) focusStep("client");
  }, [clientError, focusStep]);

  const canGoForwardFrom = (step: NewCaseStep) =>
    !isSubmitting &&
    (flow.isLastStep(step) ? flow.canSubmit : canAdvance(step, values));

  const goForwardFrom = (step: NewCaseStep) => {
    if (!canGoForwardFrom(step)) return;
    if (flow.isLastStep(step)) {
      submitForm();
      return;
    }
    confirm(step);
  };

  const chooseMode = (mode: ClientMode) => {
    if (values.mode !== mode) {
      setClientError(null);
      setFieldValue("createdClient", null, false);
      unconfirm("client");
    }
    setFieldValue("mode", mode, false);
    confirm("clientMode");
  };

  const switchToExistingClient = () => {
    setClientError(null);
    setClientQuery(values.email.trim());
    setFieldValue("mode", "existing", false);
    setFieldValue("client", null, false);
    setFieldValue("createdClient", null, false);
    unconfirm("client");
    focusStep("client");
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // An open select consumes Enter to pick an option.
    if (event.defaultPrevented) return;
    const step = flow.activeStep;
    if (step === "clientMode") {
      const option = CLIENT_MODE_OPTIONS.find((o) => o.shortcut === event.key);
      if (option) {
        event.preventDefault();
        chooseMode(option.mode);
        return;
      }
    }
    if (event.key !== "Enter" || event.shiftKey) return;
    if (!canGoForwardFrom(step)) return;
    event.preventDefault();
    goForwardFrom(step);
  };

  const baseVisa = baseVisaOf(values);

  const questionFor = (
    step: NewCaseStep
  ): { title: string; description?: string; body: ReactNode } | null => {
    switch (step) {
      case "clientMode":
        return {
          title: "Who is this case for?",
          body: <ClientModeStep value={values.mode} onChoose={chooseMode} />
        };
      case "client":
        return values.mode === "existing"
          ? {
              title: "Which client?",
              body: (
                <ExistingClientStep
                  value={values.client}
                  onChange={(client) => setFieldValue("client", client, false)}
                  query={clientQuery}
                  onQueryChange={setClientQuery}
                  onSwitchToNew={() => chooseMode("new")}
                />
              )
            }
          : {
              title: "Tell us about the client",
              description:
                "Name and email are enough to start; you can add the rest later.",
              body: (
                <NewClientStep
                  error={clientError}
                  onUseExistingClient={switchToExistingClient}
                />
              )
            };
      case "workflow":
        return {
          title: "What are you filing?",
          description:
            "Answering an RFE, NOID or NOIR? Pick it under Respond to a Notice; we'll ask for the notice next.",
          body: <WorkflowStep customTypes={customTypes} />
        };
      case "notice":
        return {
          title: `Upload the ${noticeWorkflowLabel(values.workflow)}`,
          body: <NoticeStep />
        };
      case "noticeVisa":
        return {
          title: `Which filing is this ${noticeLabelOf(values)} about?`,
          body: <NoticeVisaStep />
        };
      case "petitioner":
        return {
          title: "Who is the petitioner?",
          description: petitionerHint(baseVisa),
          body: <PetitionerStep visa={baseVisa} firmName={firmName} />
        };
      case "details":
        return {
          title: isNoticeWorkflow(values.workflow)
            ? "Response due and team"
            : "Deadline and team",
          description:
            "All optional. You can change these later from the case header.",
          body: <DetailsStep />
        };
      default:
        return null;
    }
  };

  if (layout === "dialog") {
    const onClientScreen =
      flow.activeStep === "clientMode" || flow.activeStep === "client";
    const clientShown = flow.visibleSteps.includes("client");
    // The first screen answers both client questions; OK moves on from the
    // furthest one on screen.
    const step: NewCaseStep = onClientScreen
      ? clientShown
        ? "client"
        : "clientMode"
      : flow.activeStep;
    const index = flow.visibleSteps.indexOf(step);
    const previous = onClientScreen ? null : flow.visibleSteps[index - 1];
    const question = onClientScreen ? null : questionFor(step);

    const onDialogKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.defaultPrevented) return;
      if (onClientScreen && !isTypingTarget(event.target)) {
        const option = CLIENT_MODE_OPTIONS.find(
          (o) => o.shortcut === event.key
        );
        if (option) {
          event.preventDefault();
          chooseMode(option.mode);
          return;
        }
      }
      if (event.key !== "Enter" || event.shiftKey) return;
      if (!canGoForwardFrom(step)) return;
      event.preventDefault();
      goForwardFrom(step);
    };

    return (
      <NewCaseDialogFrame
        ref={frameRef}
        firmName={firmName}
        progress={flow.progress}
        hint={
          onClientScreen
            ? `Press 1 or 2 to choose${
                clientTotal !== null
                  ? ` · ${clientTotal} client${clientTotal === 1 ? "" : "s"}`
                  : ""
              }`
            : null
        }
        onBack={previous ? () => focusStep(previous) : undefined}
        onCancel={() => onCancel?.()}
        primaryLabel={flow.isLastStep(step) ? "Start Case" : "OK"}
        onPrimary={() => goForwardFrom(step)}
        isPrimaryDisabled={isLoadingClient || !canGoForwardFrom(step)}
        isPrimaryLoading={flow.isLastStep(step) && isSubmitting}
        onKeyDown={onDialogKeyDown}
      >
        {isLoadingClient ? (
          <div className="flex justify-center py-10">
            <Spinner className="size-5 text-fg-3" />
          </div>
        ) : onClientScreen ? (
          <DialogQuestion
            ref={flow.sectionRef("clientMode")}
            stepNumber={1}
            title="Who is this case for?"
          >
            <ClientModeStep
              value={values.mode}
              onChoose={chooseMode}
              className="max-w-none"
            />
            {clientShown ? (
              <div ref={flow.sectionRef("client")} className="flex flex-col">
                {values.mode === "existing" ? (
                  <ExistingClientStep
                    value={values.client}
                    onChange={(client) =>
                      setFieldValue("client", client, false)
                    }
                    query={clientQuery}
                    onQueryChange={setClientQuery}
                    onSwitchToNew={() => chooseMode("new")}
                    onTotalChange={setClientTotal}
                    className="max-w-none"
                  />
                ) : (
                  <>
                    <p className="mt-2 text-base text-fg-3">
                      Name and email are enough to start; you can add the rest
                      later.
                    </p>
                    <NewClientStep
                      error={clientError}
                      onUseExistingClient={switchToExistingClient}
                      className="mt-4"
                    />
                  </>
                )}
              </div>
            ) : null}
          </DialogQuestion>
        ) : question ? (
          <DialogQuestion
            key={step}
            ref={flow.sectionRef(step)}
            stepNumber={index}
            title={question.title}
            description={question.description}
          >
            {question.body}
          </DialogQuestion>
        ) : null}
      </NewCaseDialogFrame>
    );
  }

  return (
    <TypeformShell progress={flow.progress} onKeyDown={handleKeyDown}>
      {isLoadingClient ? (
        <div className="flex justify-center">
          <Spinner className="size-6 text-ink-brand" />
        </div>
      ) : (
        flow.visibleSteps.map((step, index) => {
          const question = questionFor(step);
          if (!question) return null;
          const isLast = flow.isLastStep(step);
          return (
            <TypeformQuestion
              key={step}
              stepNumber={index + 1}
              title={question.title}
              description={question.description}
              isActive={flow.activeStep === step}
              onActivate={() => flow.activate(step)}
              sectionRef={flow.sectionRef(step)}
              actions={
                <TypeformActions
                  primaryLabel={isLast ? "Start Case" : "OK"}
                  onPrimary={() => goForwardFrom(step)}
                  isPrimaryDisabled={!canGoForwardFrom(step)}
                  isPrimaryLoading={isLast && isSubmitting}
                  onBack={
                    index > 0
                      ? () => focusStep(flow.visibleSteps[index - 1])
                      : undefined
                  }
                />
              }
            >
              <div className="mt-6">{question.body}</div>
            </TypeformQuestion>
          );
        })
      )}
    </TypeformShell>
  );
};

type Props = {
  currentUserId: string;
  /** Preselect this client and start at the workflow question. */
  initialClientId?: string | null;
  /** `page`: the stacked typeform; `dialog`: one question at a time in the New Case modal. */
  layout?: NewCaseLayout;
  /** The breadcrumb chip in the modal. */
  firmName?: string | null;
  /** The modal's Cancel. */
  onCancel?: () => void;
  /** The firm's saved custom case types, offered with the workflows. */
  customTypes?: readonly CustomCaseType[];
};

/**
 * New case, client first: new or existing client, then their details or a
 * pick from the client list, then the workflow (with the original filing for
 * RFE and NOID/NOIR responses), an optional petitioner, and the optional
 * deadline and team. Questions stack vertically, so
 * earlier answers stay on the page to scroll back and change.
 */
export const NewCaseWizard: FC<Props> = ({
  currentUserId,
  initialClientId = null,
  layout = "page",
  firmName,
  onCancel,
  customTypes
}) => {
  const submit = useSubmitNewCase(currentUserId);
  const [clientError, setClientError] = useState<ClientError | null>(null);
  const draftKey = newCaseDraftKey(currentUserId);
  // Answers typed before a reload come back; `?clientId=` still picks the client.
  const [initialValues] = useState(() =>
    decodeNewCaseDraft(
      readSessionDraft(draftKey),
      initialNewCaseValues(currentUserId)
    )
  );

  const handleSubmit = useCallback(
    async (values: NewCaseValues, helpers: FormikHelpers<NewCaseValues>) => {
      setClientError(null);
      const outcome = await submit(values, {
        onClientCreated: (client) =>
          helpers.setFieldValue("createdClient", client, false)
      });
      if (outcome.status === "created") {
        clearSessionDraft(draftKey);
      } else if (outcome.status === "clientError") {
        setClientError({
          message: outcome.message,
          emailExists: outcome.emailExists
        });
      } else if (outcome.status === "caseError") {
        const saved = outcome.createdClient;
        toast.error("Couldn't create the case", {
          description: saved
            ? `${outcome.message} ${saved.name} is saved as a client; Start Case again uses them.`
            : outcome.message
        });
      }
    },
    [draftKey, submit]
  );

  return (
    <Formik<NewCaseValues>
      initialValues={initialValues}
      validate={validateNewCaseValues}
      validateOnChange
      onSubmit={handleSubmit}
    >
      <>
        <KeepDraft draftKey={draftKey} />
        <WizardSteps
          initialClientId={initialClientId}
          clientError={clientError}
          setClientError={setClientError}
          layout={layout}
          firmName={firmName}
          customTypes={customTypes}
          onCancel={() => {
            clearSessionDraft(draftKey);
            onCancel?.();
          }}
        />
      </>
    </Formik>
  );
};
